import { useState, useMemo, useEffect } from 'react';
import {
  useTable,
  usePagination,
  type TableInstance,
  type Column,
  type UsePaginationInstanceProps,
  type UsePaginationState,
  type TableOptions,
  type Row,
} from 'react-table';
import Pagination from '../Pagination';
import {
  Box,
  Flex,
  HStack,
  Input,
  InputGroup,
  InputLeftElement,
  Spinner,
  Text,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
} from '@chakra-ui/react';
import HamburgerIcon from '@/assets/svg/hamburger.svg?react';
import SearchIcon from '@/assets/svg/search.svg?react';
import Button from '../Button';
import React from 'react';

export interface RoleTitle {
  id: number;
  name: string;
  description: string;
}

interface ActionMenuItem {
  label: string;
  icon?: React.ComponentType;
  onClick: (rowId: string) => void;
  isNegative?: boolean;
}

interface DatatableProps<T extends object> {
  data: T[];
  columns: Column<T>[];
  headerButton?: React.ReactNode;
  pagination?: {
    page: number;
    pageSize: number;
    totalCount: number;
    totalPages: number;
  };
  onSearch?: (searchTerm: string) => void;
  onPageChange?: (page: number) => void;
  isLoading?: boolean;
  isError?: boolean;
  sticky?: boolean;
  hideFilters?: boolean;
  headerText?: string;
  actionMenuItems?: ActionMenuItem[];
  getActionMenuItems?: (row: T) => ActionMenuItem[];
  rowIdAccessor?: keyof T | ((row: T) => string);
  customFilter?: React.ReactNode;
  customButton?: {
    text: string;
    onClick?: () => void;
    isLoading?: boolean;
  };
  filterPlaceholder?: string;
  showBothFilterAndButton?: boolean;
  searchValue?: string;
  createBtnMaxW?: string;
  headerCustomWidth?: string;
  getRowProps?: (row: Row<T>) => object; // Added getRowProps
  showSearch?: boolean;
}

const NormalDatatable = <T extends object>({
  data,
  isLoading,
  isError,
  columns,
  pagination,
  onSearch,
  onPageChange,
  headerButton,
  sticky,
  hideFilters,
  headerText,
  actionMenuItems,
  getActionMenuItems,
  rowIdAccessor,
  customFilter,
  customButton,
  filterPlaceholder,
  showBothFilterAndButton = false,
  searchValue,
  createBtnMaxW = '173px',
  headerCustomWidth = '100px',
  getRowProps, // Added to destructuring
  showSearch = true,
}: DatatableProps<T>) => {
  const [searchTerm, setSearchTerm] = useState<string>(searchValue || '');

  useEffect(() => {
    if (searchValue !== undefined) {
      setSearchTerm(searchValue);
    }
  }, [searchValue]);

  const enhancedColumns: Column<T>[] = useMemo(
    () => [
      ...columns,
      ...(actionMenuItems || getActionMenuItems
        ? [
            {
              Header: '',
              accessor: 'action' as keyof T,
              Cell: ({ row }: { row: Row<T> }) => {
                const rowId =
                  typeof rowIdAccessor === 'function'
                    ? rowIdAccessor(row.original)
                    : rowIdAccessor
                      ? String(row.original[rowIdAccessor])
                      : String(row.index);
                const menuItems = getActionMenuItems
                  ? getActionMenuItems(row.original)
                  : actionMenuItems || [];
                return (
                  <Menu>
                    <MenuButton
                      color="brand.primary.DEFAULT"
                      _hover={{ color: 'brand.primary.600' }}
                      aria-label="More actions"
                      mt={2}
                      p={0}
                    >
                      <HamburgerIcon width="20px" height="20px" />
                    </MenuButton>
                    <MenuList
                      minWidth="166px"
                      border="1px solid #D0D5DD"
                      boxShadow="0px 1px 2px 0px #1018280D"
                      borderRadius="md"
                      py={0}
                      overflow="hidden"
                    >
                      {menuItems.map((item, index) => (
                        <MenuItem
                          key={index}
                          icon={
                            item.icon ? (
                              <Box
                                width="16px"
                                height="16px"
                                display="inline-flex"
                                alignItems="center"
                                justifyContent="center"
                              >
                                <item.icon />
                              </Box>
                            ) : undefined
                          }
                          onClick={() => item.onClick(String(rowId))}
                          fontSize="sm"
                          color={item.isNegative ? 'border.error' : 'text.primary'}
                          px={4}
                          height="40px"
                          py={2}
                          _hover={{ bg: '#F9FAFB' }}
                          borderBottom={
                            index !== menuItems.length - 1 ? '1px solid #D0D5DD' : 'none'
                          }
                        >
                          {item.label}
                        </MenuItem>
                      ))}
                    </MenuList>
                  </Menu>
                );
              },
            },
          ]
        : []),
    ],
    [columns, actionMenuItems, getActionMenuItems, rowIdAccessor],
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    prepareRow,
    canPreviousPage,
    gotoPage,
    previousPage,
    nextPage,
    state: { pageIndex },
  } = useTable<T>(
    {
      columns: enhancedColumns,
      data,
      manualPagination: true,
      pageCount: pagination?.totalPages ?? -1,
      initialState: {
        pageIndex: pagination?.page ? pagination.page - 1 : 0,
        pageSize: pagination?.pageSize || 10,
      } as Partial<UsePaginationState<T>>,
    } as TableOptions<T>,
    usePagination,
  ) as TableInstance<T> & UsePaginationInstanceProps<T> & { state: UsePaginationState<T> };

  const handleSearchChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const searchValue = event.target.value;
      setSearchTerm(searchValue);
      if (onSearch) {
        onSearch(searchValue);
      }
    },
    [onSearch],
  );

  return (
    <Box
      bg={'background.white'}
      sx={{
        border: '1px solid',
        borderColor: 'border.default60',
        boxShadow: '0px 1px 2px 0px #1018280D',
        borderRadius: 'xl',
      }}
      py={hideFilters ? 0 : 6}
    >
      {headerText && (
        <Flex
          bg={'white'}
          alignItems={{ base: 'start', lg: 'center' }}
          flexDirection={{ base: 'column', lg: 'row' }}
          px={6}
          py={4}
          gap={2}
          borderRadius={'xl'}
        >
          <Text
            fontSize={'md'}
            color={'text.primary'}
            fontFamily={'primary'}
            fontWeight={'semibold'}
          >
            {headerText}
          </Text>
        </Flex>
      )}
      {hideFilters !== true && (
        <Flex
          bg={'white'}
          justifyContent={showSearch ? 'space-between' : 'end'}
          alignItems={{ base: 'start', lg: 'center' }}
          flexDirection={{ base: 'column', lg: 'row' }}
          mb={4}
          px={6}
          gap={2}
        >
          {showSearch && (
            <HStack gap={2} width={{ base: '100%', lg: 'auto' }} alignItems="center">
              <InputGroup width={{ base: '100%', sm: '334px' }} height={'40px'}>
                <InputLeftElement pointerEvents="none">
                  <SearchIcon color="gray.500" />
                </InputLeftElement>
                <Input
                  value={searchTerm}
                  onChange={handleSearchChange}
                  placeholder={filterPlaceholder || 'Search name, email address...'}
                  borderColor="border.default"
                  focusBorderColor="brand.primary.DEFAULT"
                  rounded={'xl'}
                />
              </InputGroup>
            </HStack>
          )}
          <HStack
            gap={2}
            justifyContent="flex-end"
            width={{ base: '100%', lg: 'auto' }}
            spacing={2}
          >
            {headerButton && headerButton}
            {showBothFilterAndButton && customFilter && customButton ? (
              <>
                <Box
                  width={{ base: '100%', sm: 'auto' }}
                  minWidth={{ sm: '120px' }}
                  maxWidth={{ sm: '160px' }}
                  p={0}
                  m={0}
                >
                  {customFilter}
                </Box>
                <Button
                  onClick={customButton.onClick}
                  variant={'primary'}
                  isLoading={customButton.isLoading || false}
                  minWidth={{ sm: '120px' }}
                  px={'26px'}
                  py={'10px'}
                  m={0}
                  maxWidth={createBtnMaxW}
                  maxHeight={'40px'}
                  fontSize={'sm'}
                  lineHeight={'20px'}
                >
                  {customButton.text}
                </Button>
              </>
            ) : customButton && !showBothFilterAndButton ? (
              <Button
                onClick={customButton.onClick}
                variant={'primary'}
                isLoading={customButton.isLoading || false}
                minWidth={{ sm: '120px' }}
                px={4}
                m={0}
                fontSize={'sm'}
                lineHeight={'20px'}
              >
                {customButton.text}
              </Button>
            ) : customFilter && !showBothFilterAndButton ? (
              <Box
                width={{ base: '100%', sm: 'auto' }}
                minWidth={{ sm: '120px' }}
                maxWidth={{ sm: '160px' }}
                p={0}
                m={0}
              >
                {customFilter}
              </Box>
            ) : (
              <></>
            )}
          </HStack>
        </Flex>
      )}

      <Box
        overflowX={'auto'}
        borderBottom={'1px'}
        borderColor={'border.default'}
        sx={{
          '&::-webkit-scrollbar': {
            height: '4px',
          },
          '&::-webkit-scrollbar-track': {
            background: 'background.secondary',
            borderRadius: '10px',
          },
          '&::-webkit-scrollbar-thumb': {
            background: 'brand.primary.300',
            borderRadius: '10px',
          },
          '&::-webkit-scrollbar-thumb:hover': {
            background: 'brand.primary.400',
          },
        }}
      >
        <table
          {...getTableProps()}
          style={{
            width: '100%',
            borderCollapse: 'collapse',
            fontFamily: `'Josefin Sans', sans-serif`,
            color: '#090F1A',
            fontSize: '14px',
          }}
          className={sticky ? 'datatable-sticky' : ''}
        >
          <thead style={{ backgroundColor: '#f4f4f4' }}>
            {headerGroups.map((headerGroup) => {
              const { key: headerGroupKey, ...headerGroupProps } =
                headerGroup.getHeaderGroupProps();

              return (
                <tr key={headerGroupKey} {...headerGroupProps}>
                  {headerGroup.headers.map((column, index) => {
                    const { key, ...headerProps } = column.getHeaderProps();

                    return (
                      <th
                        key={key}
                        {...headerProps}
                        style={{
                          borderTop: hideFilters !== true ? '1px solid #D0D5DD66' : 'none',
                          paddingLeft: index === 0 ? '40px' : '24px',
                          paddingRight: '24px',
                          paddingTop: '8px',
                          paddingBottom: '8px',
                          backgroundColor: 'background.tertiary',
                          textAlign: 'left',
                          fontSize: '12px',
                          background: '#f2f4f7',
                          zIndex: 2,
                          fontWeight: '500',
                          color: '#4A5769',
                          width:
                            column.id === 'product_name'
                              ? '300px'
                              : index === headerGroup.headers.length - 1
                                ? headerCustomWidth
                                : 'fit-content',
                          minWidth:
                            column.id === 'product_name'
                              ? '300px'
                              : index === headerGroup.headers.length - 1
                                ? '100px'
                                : 'fit-content',
                          maxWidth: column.id === 'product_name' ? '300px' : 'fit-content',
                        }}
                      >
                        {column.render('Header') as React.ReactNode}
                      </th>
                    );
                  })}
                </tr>
              );
            })}
          </thead>
          <tbody {...getTableBodyProps()}>
            {isLoading ? (
              <tr>
                <td
                  colSpan={enhancedColumns.length}
                  style={{ textAlign: 'center', padding: '24px' }}
                >
                  <Flex
                    justifyContent="center"
                    align="center"
                    height={{ base: '100px', sm: '110px', md: '90px' }}
                  >
                    <Spinner size={{ base: 'sm', md: 'md' }} color="brand.primary.DEFAULT" />
                  </Flex>
                </td>
              </tr>
            ) : isError ? (
              <tr>
                <td
                  colSpan={enhancedColumns.length}
                  style={{ textAlign: 'center', padding: '24px' }}
                >
                  <Text color={'text.disable'}>Something went wrong while fetching data.</Text>
                </td>
              </tr>
            ) : data.length === 0 ? (
              <tr>
                <td
                  colSpan={enhancedColumns.length}
                  style={{
                    textAlign: 'center',
                    padding: '16px',
                    color: '#6B7280',
                    fontSize: '14px',
                    fontWeight: '500',
                  }}
                >
                  No data available
                </td>
              </tr>
            ) : (
              page.map((row, index) => {
                prepareRow(row);
                const isLastRow = index === page.length - 1;

                const { key: rowKey, ...rowProps } = row.getRowProps(
                  getRowProps ? getRowProps(row) : undefined,
                );

                return (
                  <tr
                    key={rowKey}
                    {...rowProps}
                    style={{
                      borderBottom:
                        isLastRow && (pagination?.totalPages ?? 0) > 1
                          ? '1px solid #D0D5DD66'
                          : 'none',
                      backgroundColor: 'transparent',
                    }}
                  >
                    {row.cells.map((cell, cellIndex) => {
                      const { key: cellKey, ...cellProps } = cell.getCellProps();

                      return (
                        <td
                          key={cellKey}
                          {...cellProps}
                          style={{
                            borderTop: '1px solid #D0D5DD66',
                            paddingLeft: cellIndex === 0 ? '40px' : '24px',
                            paddingRight: '24px',
                            paddingTop: '15px',
                            paddingBottom: '15px',
                            color: 'text.primary',
                            fontSize: '12px',
                            fontWeight: '400',
                            width: cellIndex === row.cells.length - 1 ? '100px' : 'auto',
                            minWidth: cellIndex === row.cells.length - 1 ? '100px' : 'auto',
                            textAlign: cellIndex === 0 ? 'left' : 'left',
                          }}
                        >
                          {cell.render('Cell') as React.ReactNode}
                        </td>
                      );
                    })}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </Box>

      <HStack width={'100%'} px={6}>
        {pagination && pagination.totalPages > 1 && (
          <Pagination
            currentPage={pageIndex + 1}
            pageCount={pagination.totalPages}
            canPreviousPage={canPreviousPage}
            canNextPage={pageIndex < pagination.totalPages - 1}
            previousPage={previousPage}
            nextPage={nextPage}
            pageButtons={Array.from({ length: pagination.totalPages }, (_, index) => index + 1)}
            setPage={(page: number) => {
              gotoPage(page - 1);
              if (onPageChange) {
                onPageChange(page);
              }
            }}
          />
        )}
      </HStack>
    </Box>
  );
};

export default NormalDatatable;
